/**
 * Reading a payer's period charges for its Settings section (V1 completion G5): one sequenced read of the composed view,
 * read again after any command (a charge added, ended or withdrawn, usage recorded) so what is on screen follows what
 * the sales ledger now says. Headless: the shell renders it.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useDataAdapter } from "../data/DataAdapterContext";
import type { PeriodChargesView } from "../data/period-charges-types";
import { useChangeVersion } from "./object-ui/hooks";

export type PeriodChargesLoad =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; value: PeriodChargesView };

export function usePeriodCharges(billingAccountRef: string): {
  load: PeriodChargesLoad;
  reload: () => void;
} {
  const adapter = useDataAdapter();
  const version = useChangeVersion();
  const [nonce, setNonce] = useState(0);
  const [load, setLoad] = useState<PeriodChargesLoad>({ kind: "loading" });
  const sequence = useRef(0);
  const lastRef = useRef<string | null>(null);

  useEffect(() => {
    const mine = ++sequence.current;
    // Keep what is shown while reading the same payer again (after a command); another payer starts afresh.
    const isSamePayer = lastRef.current === billingAccountRef;
    lastRef.current = billingAccountRef;
    setLoad((current) =>
      isSamePayer && current.kind === "loaded" ? current : { kind: "loading" },
    );
    adapter
      .getPeriodCharges(billingAccountRef)
      .then((value) => {
        if (mine === sequence.current) setLoad({ kind: "loaded", value });
      })
      .catch((err: unknown) => {
        if (mine !== sequence.current) return;
        console.error("period_charges.read.failed", { billingAccountRef });
        setLoad({
          kind: "failed",
          message:
            err instanceof Error && err.message
              ? err.message
              : "Could not load the period charges.",
        });
      });
  }, [adapter, billingAccountRef, version, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { load, reload };
}
